import React, { useEffect, useState } from "react";
import { Button, message, Input, DatePicker, Select, Space } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined, EyeOutlined, FileTextOutlined } from "@ant-design/icons";
import SaleDrawer from "./SaleDrawer";
import InvoicePreview from "./InvoicePreview";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { Outlet, useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getUserRole } from "../../utils/auth";

// Extend Day.js with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function Sale() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [stores, setStores] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    storeName: null, // Will be set after fetching stores
    fromDate: null,
    toDate: null,
    financialYear: null, // New filter for financial year
  });
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const navigate = useNavigate();

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value || null };
    console.log("Filter changed:", key, value, "New filters:", newFilters);
    setFilters(newFilters);
    // Reset pagination to page 1 and fetch fresh data when any filter changes
    setPaginationInfo(prev => ({ ...prev, current: 1 }));
    // Fetch fresh data immediately with the new filters and reset pagination
    getSales(1, paginationInfo.pageSize, newFilters);
  };

  const fetchStores = async () => {
    try {
      const storesResponse = await API.get("/stores");
      setStores(storesResponse?.data || []);

      // Set initial storeName based on userData for non-admin users
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();

      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = storesResponse.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to load stores.");
      setStores([]);
    }
  };

  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      // If before April, the current financial year is the previous year
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      // If April or later, the current financial year is this year
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024; // Start from 2024
    let endYear = startYear + 1;

    // Generate financial years from 2024-2025 up to the current financial year
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears; // Return in ascending order (oldest to newest)
  };

  useEffect(() => {
    fetchStores();
    setUserRole(getUserRole());
  }, []);

  // Initial data fetch when component mounts
  useEffect(() => {
    if (stores.length > 0) {
      console.log("Initial data fetch with filters:", filters);
      getSales(1, paginationInfo.pageSize, filters);
    }
  }, [stores]);

  const handleSearchChange = (filterName, value) => {
    const newFilters = { ...filters, [filterName]: value || null };
    console.log("Store filter changed:", filterName, value, "New filters:", newFilters);
    setFilters(newFilters);
    // Reset pagination to page 1 and fetch fresh data when any filter changes
    setPaginationInfo(prev => ({ ...prev, current: 1 }));
    // Fetch fresh data immediately with the new filters and reset pagination
    getSales(1, paginationInfo.pageSize, newFilters);
  };

  const getSales = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      console.log("Fetching sales with filters:", filters);
      const { storeName, search, fromDate, toDate, customerName, sellerName, employeeName, financialYear } = filters;
      const response = await API.get("/sales/paginate", {
        params: {
          page,
          limit: pageSize,
          storeName,
          search,
          customerName,
          employeeName,
          sellerName,
          fromDate: fromDate || null, // Dates are already formatted in the onChange handler
          toDate: toDate || null,     // Dates are already formatted in the onChange handler
          financialYear: financialYear || null, // Pass financial year to API
        },
      });

      const salesData = Array.isArray(response?.data?.sales) ? response.data.sales : [];
      setSales(salesData);
      setPaginationInfo({
        current: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching sales:", err);
      message.error("Failed to load sales.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data when pagination changes (not when filters change)
    // Filter changes are handled directly in handleFilterChange and handleSearchChange
    getSales(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [paginationInfo.current, paginationInfo.pageSize]);

  const handlePaginationChange = (current, pageSize) => {
    setPaginationInfo(prev => ({ ...prev, current, pageSize }));
  };

  const handleSaveSale = async (updatedSale) => {
    getSales();
    setOpenDrawer(false);
  };

  const handleEditSale = (sale) => {
    setSelectedSale(sale);
    setOpenDrawer(true);
  };

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/sales/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const handlePreview = (sale) => {
    setSelectedInvoiceForPreview(sale);
    setIsPreviewModalVisible(true);
  };

  const handleNewInvoiceDownload = (sale) => {
    // Open invoice in new tab
    window.open(`/invoice-template/${sale._id}`, '_blank');
  };

  const handleDeleteSale = async () => {
    try {
      await API.delete(`/sales/${saleToDelete}`);
      message.success("Sale deleted successfully!");
      setSales((prev) => prev.filter((sale) => sale._id !== saleToDelete));
      setIsDeleteModalVisible(false);
    } catch (error) {
      message.error(error.response.data.message);
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      width: 80,
      fixed: 'left',
      render: (_, __, index) =>
        ((paginationInfo.current || 1) - 1) * (paginationInfo.pageSize || 20) + index + 1,
    },
    {
      title: "Invoice No.",
      render: (_, sale) => sale?.invoiceId,
      key: "invoiceId",
      width: 180,
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange('search', e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Invoice Date",
      key: "orderDate",
      width: 140,
      render: (_, sale) => dayjs(sale?.orderDate).format('DD-MM-YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys && selectedKeys.length > 0 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []);
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Invoice Amount (₹)",
      key: "totalAmount",
      width: 160,
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.totalAmount))}`,
    },
    {
      title: "Paid (₹)",
      key: "paidAmount",
      width: 120,
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.paidAmount))}`,
    },
    {
      title: "Balance (₹)",
      key: "balanceAmount",
      width: 140,
      render: (_, sale) => (
        <div style={{ flexDirection: 'column', fontSize: '12px' }}>
          <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontWeight: 'bold' }}>
            ₹ {new Intl.NumberFormat().format(sale?.balanceAmount || 0)}
          </span>
          {sale?.balanceAmount > 0 && (
            <>
              <br />
              <span style={{ color: "gray", fontSize: '11px' }}>Paid: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontSize: '11px' }}>
                ₹ {new Intl.NumberFormat().format(sale?.balancePaid || 0)}
              </span>
              <br />
              <span style={{ color: "gray", fontSize: '11px' }}>Remaining: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontSize: '11px' }}>
                ₹ {new Intl.NumberFormat().format((sale?.balanceAmount - sale?.balancePaid) || 0)}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Pay Mode",
      key: "paymentMethod",
      width: 90,
      render: (_, sale) => (
        <span style={{ 
          fontWeight: 'bold', 
          color: sale?.paymentMethod === 'CASH' ? '#52c41a' : '#1890ff',
          fontSize: '12px'
        }}>
          {sale?.paymentMethod?.toUpperCase() || '--'}
        </span>
      ),
    },
    {
      title: "Customer",
      key: "customerName",
      width: 180,
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {sale?.customerId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.customerId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleSearchChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Employee",
      key: "employeeName",
      width: 150,
      render: (_, sale) => (
        <div>
          <div style={{ color: 'orange',fontWeight: "bold", fontSize: "12px" }}>
            {sale?.employeeId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.employeeId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Employee"
          onChange={(e) => handleSearchChange("employeeName", e.target.value)}
          value={filters.employeeName}
          suffix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Store",
      key: "storeName",
      width: 120,
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", color:'#1591EA', fontSize: "12px" }}>
            {sale?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        let color;
        if (status === "pending") {
          color = "yellow";
        } else if (status === "completed") {
          color = "green";
        } else if (status === "cancelled") {
          color = "red";
        }
        return (
          <span style={{ color: color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: 'right',
      render: (_, sale) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(sale)}
            size="small"
            style={{ color: '#1890ff', borderColor: '#1890ff' }}
            title="Preview Invoice"
          />
          {userRole.editPermission === true && (
            <>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(sale)}
                size="small"
                style={{ color: '#52c41a', borderColor: '#52c41a' }}
                title="Download PDF Invoice"
              />
              <Button
                icon={<FileTextOutlined />}
                onClick={() => handleNewInvoiceDownload(sale)}
                size="small"
                style={{ color: '#722ed1', borderColor: '#722ed1' }}
                title="New Professional Invoice"
              />
            </>
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditSale(sale)}
            size="small"
            style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
            title="Edit Sale"
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Button type="primary" style={{ margin: 10 }} onClick={() => navigate("/saleForm")}>
          Create New Bill
        </Button>

        <Space align="end" style={{ padding: '10px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <Select
            style={{ width: 150 }}
            placeholder="Select Store"
            value={filters.storeName}
            onChange={(value) => handleSearchChange("storeName", value)}
            allowClear
            disabled={userRole.role !== 'admin'}
          >
            {stores.map((store) => (
              <Select.Option key={store._id} value={store.name}>
                {store.name}
              </Select.Option>
            ))}
          </Select>

          <DatePicker.RangePicker
            value={filters.fromDate && filters.toDate ? [dayjs(filters.fromDate), dayjs(filters.toDate)] : []}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                // Update both dates at once to prevent race conditions
                // For same-day selection, ensure we get the full day by adding time components
                const fromDateStr = dates[0].format("YYYY-MM-DD");
                const toDateStr = dates[1].format("YYYY-MM-DD");
                
                const newFilters = {
                  ...filters,
                  fromDate: fromDateStr,
                  toDate: toDateStr
                };
                console.log("Date range selected:", fromDateStr, "to", toDateStr, "New filters:", newFilters);
                console.log("Raw dates:", dates[0].toDate(), dates[1].toDate());
                console.log("Same day check:", fromDateStr === toDateStr);
                
                // Special handling for same-day selection
                if (fromDateStr === toDateStr) {
                  console.log("Same day selection detected - ensuring full day coverage");
                }
                setFilters(newFilters);
                setPaginationInfo(prev => ({ ...prev, current: 1 }));
                getSales(1, paginationInfo.pageSize, newFilters);
              } else {
                // Clear both dates at once
                const newFilters = {
                  ...filters,
                  fromDate: null,
                  toDate: null
                };
                console.log("Date range cleared, New filters:", newFilters);
                setFilters(newFilters);
                setPaginationInfo(prev => ({ ...prev, current: 1 }));
                getSales(1, paginationInfo.pageSize, newFilters);
              }
            }}
            format="YYYY-MM-DD"
            allowClear
          />

          <Select
            style={{ width: 150 }}
            placeholder="Select Financial Year"
            allowClear
            onChange={(value) => handleFilterChange("financialYear", value)}
            value={filters.financialYear}
          >
            {generateFinancialYears().map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Space>

      <div style={{ 
        overflowX: 'auto',
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        background: '#fff'
      }}>
        <CustomTable
          columns={columns}
          dataSource={sales}
          onPaginationChange={(pagination) =>
            setPaginationInfo(prev => ({
              ...prev,
              current: pagination.current,
              pageSize: pagination.pageSize,
            }))
          }
          loading={loading}
          style={{ 
            padding: '0 20px',
            minWidth: '1280px' // Ensure minimum width for all columns
          }}
          scroll={{ x: 1280 }} // Enable horizontal scroll
          total={paginationInfo.total}
        />
      </div>
      <SaleDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        data={selectedSale}
        onSave={handleSaveSale}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteSale}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this Sale? This action cannot be undone."
      />

      <InvoicePreview
        visible={isPreviewModalVisible}
        onClose={() => setIsPreviewModalVisible(false)}
        invoiceData={selectedInvoiceForPreview}
      />
    </div>
  );
}